import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dropdown: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    boxShadow: theme.shadows[8],
    maxHeight: 200,
    overflowY: 'auto',
    zIndex: 1000,
    minWidth: 250
  },
  listItem: {
    padding: theme.spacing(1, 2),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '& .MuiListItemText-secondary': {
        color: theme.palette.primary.contrastText
      }
    }
  },
  tagName: {
    fontWeight: 'bold',
    color: theme.palette.primary.main
  },
  description: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  }
}));

// Base de dados de tags HTML e atributos
const HTML_SUGGESTIONS = {
  tags: [
    { name: 'div', description: 'Container genérico', attributes: ['id', 'class', 'style'] },
    { name: 'span', description: 'Container inline', attributes: ['id', 'class', 'style'] },
    { name: 'p', description: 'Parágrafo', attributes: ['id', 'class', 'style'] },
    { name: 'h1', description: 'Cabeçalho nível 1', attributes: ['id', 'class', 'style'] },
    { name: 'h2', description: 'Cabeçalho nível 2', attributes: ['id', 'class', 'style'] },
    { name: 'h3', description: 'Cabeçalho nível 3', attributes: ['id', 'class', 'style'] },
    { name: 'h4', description: 'Cabeçalho nível 4', attributes: ['id', 'class', 'style'] },
    { name: 'h5', description: 'Cabeçalho nível 5', attributes: ['id', 'class', 'style'] },
    { name: 'h6', description: 'Cabeçalho nível 6', attributes: ['id', 'class', 'style'] },
    { name: 'a', description: 'Link', attributes: ['href', 'target', 'id', 'class', 'style'] },
    { name: 'img', description: 'Imagem', attributes: ['src', 'alt', 'width', 'height', 'id', 'class', 'style'], selfClosing: true },
    { name: 'br', description: 'Quebra de linha', attributes: [], selfClosing: true },
    { name: 'hr', description: 'Linha horizontal', attributes: ['id', 'class', 'style'], selfClosing: true },
    { name: 'strong', description: 'Texto forte/negrito', attributes: ['id', 'class', 'style'] },
    { name: 'em', description: 'Texto enfatizado/itálico', attributes: ['id', 'class', 'style'] },
    { name: 'b', description: 'Texto negrito', attributes: ['id', 'class', 'style'] },
    { name: 'i', description: 'Texto itálico', attributes: ['id', 'class', 'style'] },
    { name: 'u', description: 'Texto sublinhado', attributes: ['id', 'class', 'style'] },
    { name: 'ul', description: 'Lista não ordenada', attributes: ['id', 'class', 'style'] },
    { name: 'ol', description: 'Lista ordenada', attributes: ['id', 'class', 'style'] },
    { name: 'li', description: 'Item de lista', attributes: ['id', 'class', 'style'] },
    { name: 'table', description: 'Tabela', attributes: ['id', 'class', 'style'] },
    { name: 'tr', description: 'Linha da tabela', attributes: ['id', 'class', 'style'] },
    { name: 'td', description: 'Célula da tabela', attributes: ['colspan', 'rowspan', 'id', 'class', 'style'] },
    { name: 'th', description: 'Cabeçalho da tabela', attributes: ['colspan', 'rowspan', 'id', 'class', 'style'] },
    { name: 'form', description: 'Formulário', attributes: ['action', 'method', 'id', 'class', 'style'] },
    { name: 'input', description: 'Campo de entrada', attributes: ['type', 'name', 'value', 'placeholder', 'id', 'class', 'style'], selfClosing: true },
    { name: 'textarea', description: 'Área de texto', attributes: ['name', 'rows', 'cols', 'placeholder', 'id', 'class', 'style'] },
    { name: 'select', description: 'Lista de seleção', attributes: ['name', 'id', 'class', 'style'] },
    { name: 'option', description: 'Opção de seleção', attributes: ['value', 'selected', 'id', 'class', 'style'] },
    { name: 'button', description: 'Botão', attributes: ['type', 'onclick', 'id', 'class', 'style'] },
    { name: 'section', description: 'Seção do documento', attributes: ['id', 'class', 'style'] },
    { name: 'article', description: 'Artigo', attributes: ['id', 'class', 'style'] },
    { name: 'header', description: 'Cabeçalho', attributes: ['id', 'class', 'style'] },
    { name: 'footer', description: 'Rodapé', attributes: ['id', 'class', 'style'] },
    { name: 'nav', description: 'Navegação', attributes: ['id', 'class', 'style'] },
    { name: 'aside', description: 'Conteúdo lateral', attributes: ['id', 'class', 'style'] },
    { name: 'main', description: 'Conteúdo principal', attributes: ['id', 'class', 'style'] }
  ],
  
  attributes: {
    global: ['id', 'class', 'style', 'title', 'data-*'],
    specific: {
      'a': ['href', 'target', 'rel', 'download'],
      'img': ['src', 'alt', 'width', 'height', 'loading'],
      'input': ['type', 'name', 'value', 'placeholder', 'required', 'disabled'],
      'form': ['action', 'method', 'enctype', 'target'],
      'table': ['border', 'cellpadding', 'cellspacing'],
      'td': ['colspan', 'rowspan'],
      'th': ['colspan', 'rowspan', 'scope']
    }
  },

  cssProperties: [
    'color', 'background-color', 'font-size', 'font-family', 'font-weight',
    'margin', 'padding', 'width', 'height', 'display', 'position',
    'top', 'right', 'bottom', 'left', 'float', 'clear', 'text-align',
    'line-height', 'border', 'border-radius', 'box-shadow', 'opacity',
    'z-index', 'overflow', 'cursor', 'text-decoration', 'transform'
  ]
};

const AutoComplete = ({ 
  isVisible, 
  position, 
  context, 
  searchTerm, 
  onSelect, 
  onClose 
}) => {
  const classes = useStyles();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const listRef = useRef(null);

  // Filtrar sugestões baseado no contexto e termo de busca
  useEffect(() => {
    if (!searchTerm) {
      setSuggestions([]);
      return;
    }

    let filteredSuggestions = [];

    switch (context) {
      case 'tag':
        filteredSuggestions = HTML_SUGGESTIONS.tags.filter(tag =>
          tag.name.toLowerCase().startsWith(searchTerm.toLowerCase())
        ).map(tag => ({
          type: 'tag',
          name: tag.name,
          description: tag.description,
          completion: tag.selfClosing ? `${tag.name} />` : `${tag.name}></${tag.name}>`
        }));
        break;

      case 'attribute':
        const globalAttrs = HTML_SUGGESTIONS.attributes.global.filter(attr =>
          attr.toLowerCase().startsWith(searchTerm.toLowerCase())
        ).map(attr => ({
          type: 'attribute',
          name: attr,
          description: 'Atributo global',
          completion: `${attr}=""`
        }));
        
        filteredSuggestions = globalAttrs;
        break;

      case 'css-property':
        filteredSuggestions = HTML_SUGGESTIONS.cssProperties.filter(prop =>
          prop.toLowerCase().startsWith(searchTerm.toLowerCase())
        ).map(prop => ({
          type: 'css-property',
          name: prop,
          description: 'Propriedade CSS',
          completion: `${prop}: ;`
        }));
        break;

      default:
        break;
    }

    setSuggestions(filteredSuggestions.slice(0, 10)); // Limitar a 10 sugestões
    setSelectedIndex(0);
  }, [searchTerm, context]);

  // Manipular navegação por teclado
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isVisible || suggestions.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;

        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;

        case 'Enter':
        case 'Tab':
          event.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            onSelect(suggestions[selectedIndex]);
          }
          break;

        case 'Escape':
          event.preventDefault();
          onClose();
          break;

        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, suggestions, selectedIndex, onSelect, onClose]);

  // Scroll automático para o item selecionado
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedItem = listRef.current.children[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [selectedIndex]);

  if (!isVisible || suggestions.length === 0) {
    return null;
  }

  return (
    <Paper 
      className={classes.dropdown} 
      style={{ 
        left: position.left, 
        top: position.top 
      }}
    >
      <List ref={listRef} dense>
        {suggestions.map((suggestion, index) => (
          <ListItem
            key={`${suggestion.type}-${suggestion.name}`}
            className={`${classes.listItem} ${index === selectedIndex ? 'selected' : ''}`}
            onClick={() => onSelect(suggestion)}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center">
                  <Typography 
                    component="span" 
                    className={classes.tagName}
                  >
                    {suggestion.name}
                  </Typography>
                  <Typography 
                    component="span" 
                    variant="caption" 
                    style={{ marginLeft: 8, opacity: 0.7 }}
                  >
                    {suggestion.type}
                  </Typography>
                </Box>
              }
              secondary={suggestion.description}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

// Hook para detectar contexto de auto-complete
export const useAutoComplete = (value, cursorPosition) => {
  const [context, setContext] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    if (!value || cursorPosition < 0) {
      setContext(null);
      return;
    }

    const textBeforeCursor = value.substring(0, cursorPosition);
    
    // Detectar início de tag HTML
    const tagMatch = textBeforeCursor.match(/<(\w*)$/);
    if (tagMatch) {
      setContext('tag');
      setSearchTerm(tagMatch[1]);
      return;
    }

    // Detectar atributo HTML
    const attrMatch = textBeforeCursor.match(/<\w+[^>]*\s+(\w*)$/);
    if (attrMatch) {
      setContext('attribute');
      setSearchTerm(attrMatch[1]);
      return;
    }

    // Detectar propriedade CSS
    const cssMatch = textBeforeCursor.match(/style="[^"]*(\w+)$/);
    if (cssMatch) {
      setContext('css-property');
      setSearchTerm(cssMatch[1]);
      return;
    }

    setContext(null);
    setSearchTerm('');
  }, [value, cursorPosition]);

  return {
    context,
    searchTerm,
    position,
    setPosition
  };
};

export default AutoComplete;